import { Trans, useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { AppUpload } from '@components/antd/upload/app-upload';
import { MultiMediaDragger } from './dragger-multimedia';
import { UseUploadMediaReturn } from '../hooks/use-upload-media';

type BundleVerificationFieldsProps = {
  isIdentityFieldRequired?: boolean | null;
  isAddressFieldRequired?: boolean | null;
  identityFrontUploadMedia: UseUploadMediaReturn;
  identityBackUploadMedia: UseUploadMediaReturn;
  addressProofUploadMedia: UseUploadMediaReturn;
};

export function BundleVerificationFields({
  isIdentityFieldRequired,
  isAddressFieldRequired,
  identityFrontUploadMedia,
  identityBackUploadMedia,
  addressProofUploadMedia,
}: BundleVerificationFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      {isIdentityFieldRequired && (
        <div className="border border-gray-700 rounded-2xl p-4">
          <h4 className="font-medium text-lg text-text-900 capitalize!">{t('identity_document')}</h4>
          <p className="font-medium text-sm text-text-600 mb-4">
            {t('passport_preferable_driving_license_citizenship_or_any_other_govermental_documents')}
          </p>

          <div>
            <div className="flex gap-4 flex-col md:flex-row md:items-start">
              <div className="min-w-0 flex-1">
                <AppUpload.DraggerDocument {...identityFrontUploadMedia?.uploadProps}>
                  <AppUpload.DraggerDocumentBody
                    title={
                      <p className="ant-upload-text text-[15px]!">
                        <Trans
                          i18nKey="drop_your_id_front_or_browse"
                          components={[<span key="identity-front-browse" className="underline! text-primary!"></span>]}
                        />
                      </p>
                    }
                  />
                </AppUpload.DraggerDocument>

                {!!identityFrontUploadMedia?.fileList?.length && (
                  <div className="min-w-0 [&_.group>div:first-child]:min-w-0 [&_.group>div:first-child]:flex-1 [&_.group>div:first-child>div:last-child]:min-w-0 [&_.group>div:first-child>div:last-child>span:first-child]:block [&_.group>div:first-child>div:last-child>span:first-child]:truncate">
                    <AppUpload.DocumentFileList
                      fileList={identityFrontUploadMedia?.tempFileList}
                      handleRemoveClick={identityFrontUploadMedia?.handleRemoveClick}
                    />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <AppUpload.DraggerDocument {...identityBackUploadMedia?.uploadProps}>
                  <AppUpload.DraggerDocumentBody
                    title={
                      <p className="ant-upload-text text-[15px]!">
                        <Trans
                          i18nKey="drop_your_id_back_or_browse"
                          components={[<span key="identity-back-browse" className="underline! text-primary!"></span>]}
                        />
                      </p>
                    }
                  />
                </AppUpload.DraggerDocument>

                {!!identityBackUploadMedia?.fileList?.length && (
                  <div className="min-w-0 [&_.group>div:first-child]:min-w-0 [&_.group>div:first-child]:flex-1 [&_.group>div:first-child>div:last-child]:min-w-0 [&_.group>div:first-child>div:last-child>span:first-child]:block [&_.group>div:first-child>div:last-child>span:first-child]:truncate">
                    <AppUpload.DocumentFileList
                      fileList={identityBackUploadMedia?.tempFileList}
                      handleRemoveClick={identityBackUploadMedia?.handleRemoveClick}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="text-sm text-text-400 mt-2">{t('note_please_upload_both_side_of_the_document')}</p>
        </div>
      )}

      {isAddressFieldRequired && (
        <div className="border border-gray-700 rounded-2xl p-4">
          <h4 className="font-medium text-lg text-text-900">{capitalize(t('address_proof'))}</h4>
          <p className="font-medium text-sm text-text-600 mb-4">
            {t('utility_bills_tax_invoice_rent_receipt_title_deed_any_other_government_issued_id')}
          </p>

          <MultiMediaDragger uploadMultiMedia={addressProofUploadMedia} />
        </div>
      )}
    </>
  );
}
